<template>
  <div class="file-preview-carousel" ref="$scrollContainer">
    <TirPmScrollbar ref="$scrollbar">
      <div
        :class="['file-preview-carousel__row', { 'is-grabbing': isDown }]"
        ref="$carouselRow"
      >
        <div
          class="file-preview-carousel__item"
          :data-item-id="file.FileId"
          v-for="file in files"
          :key="file.FileId"
          @mouseup="handleClick(file)"
        >
          <TirPmFilePreview
            :file="file"
            :variant="FilePreviewVariantEnum.Small"
            :is-disabled-open="true"
            :hide-controls="true"
            :is-selected="selectedFile && selectedFile.FileId === file.FileId"
          />
        </div>
      </div>
    </TirPmScrollbar>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, PropType, ref, watch } from "vue";
import TirPmFilePreview from "../../tir-pm-file-preview.vue";
import { IFilePreview } from "../../interfaces";
import { FilePreviewVariantEnum } from "../../enums";
import { TirPmScrollbar } from "tir-pm-scrollbar";

const emit = defineEmits<{
  /**Событие выбора файла */
  (e: "selectFile", value: IFilePreview): void;
}>();

const props = defineProps({
  /**Выбранный файл */
  selectedFile: {
    type: Object as PropType<IFilePreview>,
  },
  /**Список файлов */
  files: {
    type: Array as PropType<Array<IFilePreview>>,
    required: true,
  },
  /**Скорость перетаскивания */
  dragSpeed: {
    type: Number,
    default: 1.15,
  },
});

/**Ссылка на элемент строки в карусели */
const $carouselRow = ref<HTMLElement>();
/**Ссылка на элемент скроллбара */
const $scrollbar = ref<InstanceType<typeof TirPmScrollbar>>();
const $scrollContainer = ref<HTMLElement>();

const scrollToSelectedFile = (smooth: boolean = true) => {
  if (!props.selectedFile) return;
  const scrollToElement = $carouselRow.value?.querySelector(
    `[data-item-id="${props.selectedFile.FileId}"]`
  ) as HTMLElement | undefined;
  if (!scrollToElement) return;
  $scrollbar.value.$scroll?.scrollTo({
    top: 0,
    left:
      scrollToElement.offsetLeft -
      $scrollbar.value.$scroll.offsetWidth / 2 +
      72.5,
    behavior: smooth ? "smooth" : "auto",
  });
};

watch(
  () => props.selectedFile?.FileId,
  () => scrollToSelectedFile(true)
);

const isDragging = ref(false);
const isDown = ref(false);
let startX: number;
let scrollLeft: number;
let startScrollLeft: number;

const handleMouseDown = (e: MouseEvent) => {
  isDown.value = true;
  isDragging.value = false;
  startX = e.pageX - $scrollContainer.value!.offsetLeft;
  scrollLeft = $scrollbar.value!.$scroll.scrollLeft;
  startScrollLeft = scrollLeft;
  document.body.style.cursor = "grabbing";
};

const handleMouseLeaveOrUp = () => {
  isDown.value = false;
  isDragging.value = false;
  document.body.style.removeProperty("cursor");
};

const handleMouseMove = (e: MouseEvent) => {
  if (!isDown.value) return;
  const x = e.pageX - $scrollContainer.value!.offsetLeft;
  const walk = (x - startX) * props.dragSpeed;

  $scrollbar.value!.$scroll.scrollLeft = scrollLeft - walk;
};

const handleClick = (file: IFilePreview) => {
  if (
    !isDown.value ||
    Math.abs(startScrollLeft - ($scrollbar.value?.$scroll?.scrollLeft || 0)) <= 20
  ) {
    emit("selectFile", file);
  }
};

onMounted(() => {
  scrollToSelectedFile(false);
  $scrollContainer.value?.addEventListener("mousedown", handleMouseDown);
  window.addEventListener("mouseleave", handleMouseLeaveOrUp);
  window.addEventListener("mouseup", handleMouseLeaveOrUp);
  window.addEventListener("mousemove", handleMouseMove);
});

onUnmounted(() => {
  $scrollContainer.value?.removeEventListener("mousedown", handleMouseDown);
  window.removeEventListener("mouseleave", handleMouseLeaveOrUp);
  window.removeEventListener("mouseup", handleMouseLeaveOrUp);
  window.removeEventListener("mousemove", handleMouseMove);
});
</script>

<style lang="scss" scoped>
@import "./file-preview-carousel.scss";
</style>

<style lang="scss">
.file-preview-carousel__row {
  &.is-grabbing {
    cursor: grabbing;
    * {
      cursor: grabbing;
    }
  }
}
</style>
